/**
 * Utility functions for managing document warning system
 * Tracks when documents were last opened and handles warning dismissals
 */

const DAYS_THRESHOLD = 2; // Show warning if document not opened in X days
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

/**
 * Updates the last opened time for a document
 */
export const updateDocumentLastOpened = (documentId: number): void => {
    const key = `document_last_opened_${documentId}`;
    const currentTime = new Date().getTime();
    localStorage.setItem(key, currentTime.toString());
};

/**
 * Gets the last opened time for a document
 */
export const getDocumentLastOpened = (documentId: number): number | null => {
    const key = `document_last_opened_${documentId}`;
    const lastOpened = localStorage.getItem(key);
    return lastOpened ? parseInt(lastOpened, 10) : null;
};

/**
 * Calculates the number of days since a document was last opened
 */
export const getDaysSinceLastOpened = (documentId: number): number => {
    const lastOpened = getDocumentLastOpened(documentId);
    if (!lastOpened) {
        // If never opened before, consider it as opened a long time ago
        return DAYS_THRESHOLD + 1;
    }
    
    const currentTime = new Date().getTime();
    const timeDiff = currentTime - lastOpened;
    return Math.floor(timeDiff / (24 * 60 * 60 * 1000));
};

/**
 * Checks if warning was recently dismissed for a document
 */
export const isWarningRecentlyDismissed = (documentId: number): boolean => {
    const key = `unpublished_warning_dismissed_${documentId}`;
    const dismissTime = localStorage.getItem(key);
    
    if (!dismissTime) {
        return false;
    }
    
    const currentTime = new Date().getTime();
    const timeSinceDismiss = currentTime - parseInt(dismissTime, 10);
    
    return timeSinceDismiss < DISMISS_DURATION;
};

/**
 * Determines if the unpublished changes warning should be shown
 */
export const shouldShowUnpublishedWarning = (
    documentId: number,
    hasUnpublishedChanges: boolean
): { shouldShow: boolean; daysSinceLastOpened: number } => {
    // Don't show if document doesn't have unpublished changes
    if (!hasUnpublishedChanges) {
        return { shouldShow: false, daysSinceLastOpened: 0 };
    }
    
    // Don't show if warning was recently dismissed
    if (isWarningRecentlyDismissed(documentId)) {
        return { shouldShow: false, daysSinceLastOpened: 0 };
    }
    
    const daysSinceLastOpened = getDaysSinceLastOpened(documentId);
    
    // Show warning if document hasn't been opened in the threshold period
    const shouldShow = daysSinceLastOpened >= DAYS_THRESHOLD;
    
    return { shouldShow, daysSinceLastOpened };
};

/**
 * Constants for configuration
 */
export const UNPUBLISHED_WARNING_CONFIG = {
    DAYS_THRESHOLD,
    DISMISS_DURATION_DAYS: DISMISS_DURATION / (24 * 60 * 60 * 1000),
}; 